import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { LuChevronDown, LuChevronUp, LuCopy, LuX } from 'react-icons/lu';
import GlassIcon from '../ui/GlassIcon';
import IconButton from '../ui/IconButton';
import { GLASS_CHIP, GLASS_PRESSABLE } from '../ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';

const TINTS = {
  teal: 'tw-bg-teal-500/10 tw-text-teal-600 dark:tw-bg-teal-400/10 dark:tw-text-teal-300',
  blue: 'tw-bg-blue-500/10 tw-text-blue-600 dark:tw-bg-blue-400/10 dark:tw-text-blue-300',
  violet: 'tw-bg-violet-500/10 tw-text-violet-600 dark:tw-bg-violet-400/10 dark:tw-text-violet-300',
  indigo: 'tw-bg-indigo-500/10 tw-text-indigo-600 dark:tw-bg-indigo-400/10 dark:tw-text-indigo-300',
  amber: 'tw-bg-amber-500/10 tw-text-amber-600 dark:tw-bg-amber-400/10 dark:tw-text-amber-300',
  emerald: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-bg-emerald-400/10 dark:tw-text-emerald-300',
  rose: 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-bg-rose-400/10 dark:tw-text-rose-300',
};

const FOOTER_TONES = {
  danger: 'tw-border-rose-500/25 tw-bg-rose-500/10 tw-text-rose-700 hover:tw-bg-rose-500/15 dark:tw-border-rose-400/20 dark:tw-text-rose-300',
  success: 'tw-border-emerald-500/25 tw-bg-emerald-500/10 tw-text-emerald-700 hover:tw-bg-emerald-500/15 dark:tw-border-emerald-400/20 dark:tw-text-emerald-300',
};

const NAV_BUTTON = 'disabled:tw-pointer-events-none disabled:tw-opacity-40';

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '' || value === '-';

const DetailRow = ({ icon: Icon, label, value, href, external, copyable }) => {
  const { t } = useTranslation();
  const empty = isBlank(value);
  const text = empty ? '-' : String(value);
  const valueClass = `tw-block tw-truncate tw-text-sm tw-font-medium ${empty ? 'tw-text-slate-400 dark:tw-text-slate-500' : 'tw-text-slate-800 dark:tw-text-slate-100'}`;

  return (
    <li className="tw-group tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-px-2 tw-py-1.5 tw-transition-colors hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.04]">
      <span className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-slate-900/[0.04] tw-text-slate-400 dark:tw-bg-white/[0.06] dark:tw-text-slate-500">
        <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
      </span>
      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{label}</p>
        {href && !empty ? (
          <a href={href} title={text} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className={`${valueClass} tw-transition-colors hover:tw-text-teal-700 dark:hover:tw-text-teal-300`}>{text}</a>
        ) : (
          <span title={empty ? undefined : text} className={valueClass}>{text}</span>
        )}
      </div>
      {copyable && !empty && (
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(text);
              toast.success(t('dataTable.panel.copied', { label }));
            } catch (error) {
              toast.error(t('dataTable.panel.copyFailed'));
            }
          }}
          aria-label={t('dataTable.panel.copyLabel', { label })}
          title={t('dataTable.panel.copy')}
          className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-opacity-0 tw-transition hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 focus-visible:tw-opacity-100 group-hover:tw-opacity-100 [@media(hover:none)]:tw-opacity-100 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
        >
          <LuCopy aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        </button>
      )}
    </li>
  );
};

DetailRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  href: PropTypes.string,
  external: PropTypes.bool,
  copyable: PropTypes.bool,
};

/**
 * Summary and actions of the selected record, beside or over a data table.
 *  - identity: { avatar (initials or an icon), gradient, title, subtitle, badges (nodes) }
 *  - actions: [{ key, label, icon, tint, onClick, disabled, title, spin }]
 *  - details: [{ key, icon, label, value, href, external (new tab), copyable }]
 *  - footer: an optional wide button at the bottom ({ label, icon, tone, onClick, title })
 *  - children: optional page-specific content between the actions and the details
 * ↑/↓ step to the previous/next record of the list.
 */
const DetailPanel = forwardRef(({
  heading,
  position,
  contentKey,
  identity,
  actions = [],
  details = [],
  footer,
  onPrev,
  onNext,
  onClose,
  className = '',
  children,
}, closeButtonRef) => {
  const { t } = useTranslation();
  const FooterIcon = footer?.icon;

  return (
    <div className={`tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden tw-rounded-[1.75rem] ${className}`}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-pt-4">
        <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 tw-tabular-nums dark:tw-text-slate-500">
          {heading}{position ? ` · ${position.index + 1} / ${position.total}` : ''}
        </p>
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <IconButton label={t('dataTable.panel.previous')} onClick={onPrev} disabled={!onPrev} className={NAV_BUTTON}>
            <LuChevronUp aria-hidden="true" className="tw-h-4 tw-w-4" />
          </IconButton>
          <IconButton label={t('dataTable.panel.next')} onClick={onNext} disabled={!onNext} className={NAV_BUTTON}>
            <LuChevronDown aria-hidden="true" className="tw-h-4 tw-w-4" />
          </IconButton>
          <IconButton ref={closeButtonRef} label={t('dataTable.panel.close')} onClick={onClose}>
            <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
          </IconButton>
        </div>
      </div>

      <motion.div
        key={contentKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.18 }}
        className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col"
      >
        <div className="tw-flex tw-flex-col tw-items-center tw-px-5 tw-pb-4 tw-pt-1 tw-text-center">
          <GlassIcon gradient={identity.gradient || 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30'} size="avatarXl">
            {identity.avatar}
          </GlassIcon>
          <h2 title={identity.title} className="tw-mt-3 tw-line-clamp-2 tw-max-w-full tw-break-words tw-text-lg tw-font-semibold tw-leading-tight tw-text-slate-900 dark:tw-text-white">
            {identity.title}
          </h2>
          {identity.subtitle && (
            <p title={identity.subtitle} className="tw-mt-1 tw-max-w-full tw-truncate tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{identity.subtitle}</p>
          )}
          {identity.badges && <div className="tw-mt-3 tw-flex tw-flex-wrap tw-justify-center tw-gap-1.5">{identity.badges}</div>}
        </div>

        <div className="tw-min-h-0 tw-flex-1 tw-space-y-5 tw-overflow-y-auto tw-px-3 tw-pb-4 glass-scrollbar">
          {actions.length > 0 && (
            <section aria-label={t('dataTable.panel.actions')} className="tw-grid tw-grid-cols-2 tw-gap-2 tw-px-1">
              {actions.map((action, index) => {
                const Icon = action.icon;
                const spansRow = index === actions.length - 1 && actions.length % 2 === 1;
                return (
                  <button
                    key={action.key}
                    type="button"
                    onClick={action.onClick}
                    disabled={action.disabled}
                    title={action.title}
                    className={`tw-flex tw-min-w-0 tw-items-center tw-gap-2.5 tw-rounded-2xl tw-p-2.5 tw-text-left tw-text-sm tw-font-semibold tw-text-slate-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-70 disabled:active:tw-scale-100 dark:tw-text-slate-200 ${GLASS_CHIP} ${GLASS_PRESSABLE} ${spansRow ? 'tw-col-span-2' : ''}`}
                  >
                    <span className={`tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-xl ${TINTS[action.tint] || TINTS.teal}`}>
                      <Icon aria-hidden="true" className={`tw-h-4 tw-w-4 ${action.spin ? 'tw-animate-spin' : ''}`} />
                    </span>
                    <span className="tw-min-w-0 tw-leading-tight">{action.label}</span>
                  </button>
                );
              })}
            </section>
          )}

          {children}

          {details.length > 0 && (
            <section aria-label={t('dataTable.panel.details')} className="tw-block">
              <p aria-hidden="true" className="tw-px-2 tw-pb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
                {t('dataTable.panel.details')}
              </p>
              <ul className="tw-block">
                {details.map(({ key, ...detail }) => <DetailRow key={key} {...detail} />)}
              </ul>
            </section>
          )}
        </div>
      </motion.div>

      {footer && (
        <div className="tw-border-t tw-border-slate-200/70 tw-p-3 dark:tw-border-white/10">
          <button
            type="button"
            onClick={footer.onClick}
            title={footer.title}
            className={`tw-flex tw-h-11 tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-text-sm tw-font-semibold tw-transition-[background-color,transform] tw-duration-200 active:tw-scale-[0.98] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${FOOTER_TONES[footer.tone] || FOOTER_TONES.success}`}
          >
            {FooterIcon && <FooterIcon aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {footer.label}
          </button>
        </div>
      )}
    </div>
  );
});

DetailPanel.displayName = 'DetailPanel';

DetailPanel.propTypes = {
  heading: PropTypes.string.isRequired,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  contentKey: PropTypes.string.isRequired,
  identity: PropTypes.shape({
    avatar: PropTypes.node,
    gradient: PropTypes.string,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    badges: PropTypes.node,
  }).isRequired,
  actions: PropTypes.arrayOf(PropTypes.object),
  details: PropTypes.arrayOf(PropTypes.object),
  footer: PropTypes.object,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default DetailPanel;
